import React, { useEffect, useMemo, useRef, useState } from 'react';
import { recordHoldersAPI } from '../services/api';
import RecordCarousel from '../components/RecordCarousel';
import { sanitizeRichTextHtml } from '../lib/richText';

const getImages = (holder) => (holder?.images || []).filter(Boolean);
const getDeclarationHtml = (value) => {
  const html = sanitizeRichTextHtml(String(value || '').trim())
    .replace(/^(?:\s|<br\s*\/?>|<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>)+/gi, '')
    .replace(/(?:\s|<br\s*\/?>|<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>)+$/gi, '')
    .trim();

  if (!html) return '';
  return /^\s*<(p|ul|ol)/i.test(html) ? html : `<p>${html}</p>`;
};

const RecordsPage = () => {
  const [recordHolders, setRecordHolders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const highlightedRecordRef = useRef(null);
  const [sideListMaxHeight, setSideListMaxHeight] = useState(null);

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        setLoading(true);
        const data = await recordHoldersAPI.getAll();
        setRecordHolders(data);
        setSelectedId(data[0]?.id ?? null);
        setError('');
      } catch (err) {
        console.error('Error fetching records:', err);
        setError('Failed to load records.');
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, []);

  const selectedRecord = useMemo(
    () => recordHolders.find((holder) => holder.id === selectedId) || recordHolders[0],
    [recordHolders, selectedId]
  );

  const selectedAchievementHtml = useMemo(
    () => getDeclarationHtml(selectedRecord?.achievement),
    [selectedRecord]
  );

  useEffect(() => {
    const highlightedRecord = highlightedRecordRef.current;
    if (!highlightedRecord) return undefined;

    const syncHeights = () => {
      if (window.innerWidth < 1024) {
        setSideListMaxHeight(null);
        return;
      }

      setSideListMaxHeight(highlightedRecord.getBoundingClientRect().height);
    };

    syncHeights();
    const resizeObserver = new ResizeObserver(syncHeights);
    resizeObserver.observe(highlightedRecord);
    window.addEventListener('resize', syncHeights);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncHeights);
    };
  }, [selectedRecord]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="flex h-64 items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-red-700"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="records-page-bg min-h-screen pt-16 pb-20">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-7xl">
          <header className="mx-auto mb-12 max-w-3xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.34em] text-[#9b6a1d]">
              Swaarnim Hall of Achievement
            </p>
            <h1 className="font-calligraphic mt-3 py-2 text-6xl leading-[1.15] text-red-800 sm:text-7xl md:text-8xl">
              Records
            </h1>
            <div className="mx-auto mt-5 flex max-w-sm items-center justify-center gap-3">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-[#c59b44] to-[#c59b44]" />
              <span className="h-2 w-2 rotate-45 border border-[#c59b44] bg-white" />
              <span className="h-px flex-1 bg-gradient-to-l from-transparent via-[#c59b44] to-[#c59b44]" />
            </div>
          </header>

          {error ? (
            <div className="certificate-surface rounded-lg border border-[#d9bd77] p-12 text-center shadow-xl shadow-red-950/5">
              <p className="font-record-serif text-2xl font-semibold text-red-700">{error}</p>
            </div>
          ) : recordHolders.length === 0 ? (
            <div className="certificate-surface rounded-lg border border-[#d9bd77] p-12 text-center shadow-xl shadow-red-950/5">
              <p className="font-record-serif text-2xl font-semibold text-stone-600">No records available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
              <article
                ref={highlightedRecordRef}
                className="certificate-surface overflow-hidden rounded-lg border border-[#d8b86f] shadow-2xl shadow-red-950/10"
              >
                {getImages(selectedRecord).length > 0 && (
                  <div className="border-b border-[#e6d4a3] bg-[#fffdf7] p-3">
                    <RecordCarousel images={getImages(selectedRecord)} />
                  </div>
                )}

                <div className="p-6 md:p-9">
                  <div className={`grid gap-6 ${selectedRecord.certificate_image ? 'items-center md:grid-cols-[minmax(0,1fr)_220px]' : ''}`}>
                    <div className="flex min-h-44 flex-col items-center justify-start pt-4 text-center md:min-h-64 md:pt-8">
                      <p className="mb-8 text-xs font-bold uppercase tracking-[0.28em] text-[#a67925] md:mb-10">
                        Record Holder
                      </p>
                      <h2 className="font-record-serif pb-2 text-4xl font-bold leading-tight tracking-tight text-red-950 [overflow-wrap:anywhere] md:text-5xl">
                        {selectedRecord.name}
                      </h2>
                    </div>

                    {selectedRecord.certificate_image && (
                      <div className="mx-auto w-40 overflow-hidden rounded-md border border-[#c59b44] bg-white p-2 shadow-lg shadow-red-950/10 sm:w-52 md:mx-0">
                        <img
                          src={selectedRecord.certificate_image}
                          alt={`${selectedRecord.name} certificate`}
                          className="aspect-[3/4] w-full rounded-sm bg-[#fbf7ed] object-contain"
                        />
                      </div>
                    )}
                  </div>

                  <p className="record-title-display mt-5 whitespace-pre-line border-l-4 border-[#c59b44] bg-[#fff8e6] px-4 py-3 text-2xl font-bold leading-8 text-red-900 shadow-sm shadow-red-950/5 [overflow-wrap:anywhere] md:text-3xl md:leading-9">
                    {selectedRecord.title}
                  </p>
                  {selectedAchievementHtml && (
                    <div
                      className="rich-text-content record-declaration mt-8 rounded-md border border-[#e0c579] px-5 py-6 text-justify text-xl leading-9 text-stone-800 shadow-inner shadow-yellow-900/5 [overflow-wrap:anywhere] md:px-8 md:py-7 md:text-2xl md:leading-10"
                      dangerouslySetInnerHTML={{ __html: selectedAchievementHtml }}
                    />
                  )}
                </div>
              </article>

              <aside
                className="self-start rounded-lg border border-[#dfc57f] bg-white/90 p-4 shadow-xl shadow-red-950/5 backdrop-blur lg:overflow-y-auto"
                style={sideListMaxHeight ? { maxHeight: `${sideListMaxHeight}px` } : undefined}
              >
                <h2 className="font-record-serif mb-1 px-2 text-2xl font-bold text-stone-950">
                  More Records
                </h2>
                <div className="mb-4 ml-2 h-px w-20 bg-[#c59b44]" />
                <div className="space-y-3">
                  {recordHolders.map((holder) => {
                    const previewImage = getImages(holder)[0] || holder.certificate_image;
                    const isSelected = holder.id === selectedRecord?.id;

                    return (
                      <button
                        type="button"
                        key={holder.id}
                        onClick={() => {
                          setSelectedId(holder.id);
                          window.scrollTo({ top: 250, behavior: 'smooth' });
                        }}
                        className={`flex w-full gap-3 rounded-md border p-3 text-left transition ${
                          isSelected
                            ? 'border-[#c59b44] bg-[#fff6df] shadow-sm'
                            : 'border-stone-200 bg-white hover:border-[#d5ad54] hover:bg-[#fffaf0]'
                        }`}
                      >
                        {previewImage && (
                          <img
                            src={previewImage}
                            alt={holder.name}
                            className="h-20 w-20 flex-none rounded border border-stone-100 bg-[#fbf7ed] object-contain"
                          />
                        )}
                        <span className="min-w-0">
                          <span className="font-record-serif records-clamp-2 text-xl font-bold leading-6 text-red-950 [overflow-wrap:anywhere]">
                            {holder.name}
                          </span>
                          <span className="record-title-display records-clamp-2 mt-2 whitespace-pre-line text-base font-bold leading-5 text-[#8a5316]">
                            {holder.title}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordsPage;
